/**
 * The period export: every Beleg of a period as a file of its own, for an accountant's upload
 * portal or a manual check.
 *
 * This module decides what goes in and what it is called; `lib/api/period-export.ts` fetches the
 * files and `lib/export/pdf.ts` builds the PDFs. A Beleg gets a data page when it carries something the
 * original file does not show - a photo turned into a scan, notes (occasion and guests of a
 * Bewirtung), a tip. A supplier's PDF without any of that is passed on untouched.
 */
import type { TFunction } from "i18next";
import type { BankAccount, Transaction } from "@/lib/api/banking";
import type { Category } from "@/lib/api/categories";
import { isImage, type DocumentRecord } from "@/lib/api/documents";
import { isWithin, type Period } from "@/lib/at/periods";
import { relevantDate, type AccountingBasis } from "@/lib/at/uva";
import { parseVatLines } from "@/lib/at/vat";
import { formatDate, formatMoney, formatPercent, formatRate } from "@/lib/utils/format";
import { transactionDate } from "@/lib/banking/period-close";

/** Everything but archived documents whose date - by the accounting basis - is in the period. */
export function periodDocuments(
  documents: DocumentRecord[],
  period: Period,
  basis: AccountingBasis,
): DocumentRecord[] {
  return documents
    .filter((document) => document.status !== "archived")
    .filter((document) => isWithin(relevantDate(document, basis), period))
    .sort((a, b) => (a.docNumber ?? "").localeCompare(b.docNumber ?? ""));
}

export function needsDataPage(document: DocumentRecord): boolean {
  return (
    isImage(document) ||
    (document.originals?.length ?? 0) > 0 ||
    Boolean(document.notes?.trim()) ||
    (document.tip ?? 0) > 0
  );
}

/** "Bürobedarf Wien GmbH" -> "Buerobedarf-Wien-GmbH": safe in every upload portal and zip tool. */
export function fileSafe(value: string, max = 40): string {
  return value
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/Ä/g, "Ae")
    .replace(/Ö/g, "Oe")
    .replace(/Ü/g, "Ue")
    .replace(/ß/g, "ss")
    .normalize("NFKD")
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, max)
    .replace(/-+$/, "");
}

/** "ER/ER-2026-0042_2026-09-14_Buerobedarf-Wien-GmbH" - the path without extension. */
export function exportBaseName(document: DocumentRecord): string {
  const parts = [
    document.docNumber || `${document.kind}-${document.id}`,
    (document.issueDate || document.paidDate || "").slice(0, 10),
    fileSafe(document.partnerName ?? ""),
  ].filter(Boolean);
  return `${document.kind}/${parts.join("_")}`;
}

export function extensionOf(fileName: string): string {
  return /\.([A-Za-z0-9]+)$/.exec(fileName)?.[1]?.toLowerCase() ?? "";
}

export type DataPage = {
  title: string;
  subtitle: string;
  rows: Array<[string, string]>;
  notesLabel: string;
  notes: string;
  footer: string;
};

export type DataPageContext = {
  categories: Category[];
  transactions: Transaction[];
  accounts: BankAccount[];
  workspaceName: string;
  today: string;
};

/** What the data page says, translated. Empty values are left out rather than printed blank. */
export function dataPage(
  document: DocumentRecord,
  context: DataPageContext,
  t: TFunction,
): DataPage {
  const currency = document.currency || "EUR";
  const money = (value: number | undefined) =>
    typeof value === "number" ? formatMoney(value, currency) : "";
  const category = context.categories.find((entry) => entry.id === document.category);
  const charge = context.transactions.find((transaction) => transaction.document === document.id);
  const account = charge && context.accounts.find((entry) => entry.id === charge.account);
  const tip = document.tip ?? 0;

  const rows: Array<[string, string]> = [
    [t("export.documents.kind"), t(`kinds.${document.kind}`)],
    [t("export.documents.issueDate"), formatDate(document.issueDate)],
    [t("export.documents.serviceDate"), formatDate(document.serviceDate)],
    [t("export.documents.partnerName"), document.partnerName ?? ""],
    [t("periodExport.page.address"), document.partnerAddress ?? ""],
    [t("export.documents.partnerVatId"), document.partnerVatId ?? ""],
    [t("export.documents.invoiceNumber"), document.invoiceNumber ?? ""],
    [
      t("export.documents.category"),
      category ? [category.accountNumber, category.name].filter(Boolean).join(" ") : "",
    ],
    [
      t("export.documents.taxTreatment"),
      document.taxTreatment ? t(`taxTreatment.${document.taxTreatment}`) : "",
    ],
    ...parseVatLines(document.vatLines).map((line): [string, string] => [
      t("periodExport.page.vatLine", { rate: formatRate(line.rate) }),
      t("periodExport.page.netVat", { net: money(line.net), vat: money(line.vat) }),
    ]),
    [t("export.documents.netTotal"), money(document.netTotal)],
    [t("export.documents.vatTotal"), money(document.vatTotal)],
    [t("export.documents.grossTotal"), money(document.grossTotal)],
    [t("export.documents.tip"), tip > 0 ? money(tip) : ""],
    [t("periodExport.page.charged"), tip > 0 ? money((document.grossTotal ?? 0) + tip) : ""],
    [
      t("periodExport.page.payment"),
      [
        document.paymentMethod ? t(`paymentMethod.${document.paymentMethod}`) : "",
        document.paymentStatus ? t(`paymentStatus.${document.paymentStatus}`) : "",
        formatDate(document.paidDate),
      ]
        .filter(Boolean)
        .join(" · "),
    ],
    [
      t("periodExport.page.charge"),
      charge
        ? [
            formatDate(transactionDate(charge)),
            formatMoney(Math.abs(charge.amount), charge.currency || "EUR"),
            charge.counterparty ?? "",
            account?.label ?? "",
          ]
            .filter(Boolean)
            .join(" · ")
        : "",
    ],
    [
      t("export.documents.deductiblePercent"),
      document.deductiblePercent !== undefined && document.deductiblePercent !== 100
        ? formatPercent(document.deductiblePercent / 100)
        : "",
    ],
  ];

  return {
    title: t("periodExport.page.title"),
    subtitle: [document.docNumber, document.partnerName].filter(Boolean).join(" · "),
    rows: rows.filter(([, value]) => value.trim() !== ""),
    notesLabel: t("export.documents.notes"),
    notes: document.notes?.trim() ?? "",
    footer: t("periodExport.page.footer", {
      workspace: context.workspaceName,
      date: formatDate(context.today),
    }),
  };
}
